import { Formatter, PaymentCalculatorFactory } from '@dealersites/dealersite-js-core';
import { get } from 'lodash';

const dsp = (global.dsp = global.dsp || {});

// import Formatter from './Formatter.js';
// import FinancePaymentCalculator from './Calculator/FinancePaymentCalculator.js';
// import LeasePaymentCalculator from './Calculator/LeasePaymentCalculator.js';

/**
 * Calculates and displays leasing and financing numbers on the front end
 *
 * Example Usage:
 * let paymentCalculator = new PaymentCalculatorForm('js-calc-form');
 */
export default class PaymentCalculatorForm {
  constructor(formElementId, options = {}) {
    this.options = options;

    this.element = document.getElementById(formElementId);
    if (!this.element) {
      return;
    }

    this.log('initializing...');

    this.prefix = '#js-calc-';

    this.finance = PaymentCalculatorFactory.createCalculator('finance', {
      frequency: 'monthly',
    });
    this.lease = PaymentCalculatorFactory.createCalculator('lease', { frequency: 'monthly' });
    this.hasFinanceCalculator = true;
    this.hasLeaseCalculator = true;
    this.formatVdpPrice = dsp.formatter.price.custom(['calculator-page']);

    this.dropdown = this.element.querySelector(this.prefix + 'payment-dropdown');

    // Store reference
    this.productElements = {
      price: this.element.querySelector(this.prefix + 'price'),
      tax: this.element.querySelector(this.prefix + 'tax'),
      tradein: this.element.querySelector(this.prefix + 'tradein'),
    };

    // Store reference
    this.financeElements = {
      down: this.element.querySelector(this.prefix + 'finance-down'),
      term: this.element.querySelector(this.prefix + 'finance-term'),
      rate: this.element.querySelector(this.prefix + 'finance-rate'),
      balance: this.element.querySelector(this.prefix + 'finance-balance'),
      payment: this.element.querySelector(this.prefix + 'finance-payment'),
      cob: this.element.querySelector(this.prefix + 'finance-cob') ?? false,
    };

    // Store reference
    this.leaseElements = {
      down: this.element.querySelector(this.prefix + 'lease-down'),
      term: this.element.querySelector(this.prefix + 'lease-term'),
      rate: this.element.querySelector(this.prefix + 'lease-rate'),
      end: this.element.querySelector(this.prefix + 'lease-end'),
      balance: this.element.querySelector(this.prefix + 'lease-balance'),
      payment: this.element.querySelector(this.prefix + 'lease-payment'),
      cob: this.element.querySelector(this.prefix + 'lease-cob') ?? false,
    };

    this.hasFinanceCalculator = !Object.values(this.financeElements).some((e) => e === null);
    this.hasLeaseCalculator = !Object.values(this.leaseElements).some((e) => e === null);

    // Handle the "Submit" (calculate) button
    this.element.addEventListener('submit', (event) => {
      event.preventDefault();
      this.calculate();
    });

    // recalculate on input changes
    const inputs = this.element.querySelectorAll('input[type="text"]');
    [].forEach.call(inputs, (input) => {
      input.addEventListener('change', (e) => {
        this.calculate();
      });
    });

    const selects = this.element.querySelectorAll('select');
    [].forEach.call(selects, (input) => {
      input.addEventListener('change', (e) => {
        this.calculate();
      });
    });

    // Init with calculations already run once
    this.calculate();
  }

  /**
   * Update calculators with current values.
   */
  updateCalculators() {
    const price = this.productElements.price.value
      ? Formatter.number(this.productElements.price.value)
      : 0;
    const tradein = this.productElements.tradein.value
      ? Formatter.number(this.productElements.tradein.value)
      : 0;
    const tax = this.productElements.tax.value
      ? Formatter.number(this.productElements.tax.value)
      : 0;

    let paymentFrequency = 'monthly';
    if (this.dropdown) {
      paymentFrequency = this.dropdown.querySelector('option:checked').value;
    }

    if (this.hasFinanceCalculator) {
      // Update Finance Calculator
      this.finance.price = price;
      this.finance.tradein = tradein;
      this.finance.tax = tax;
      this.finance.down = this.financeElements.down.value
        ? Formatter.number(this.financeElements.down.value)
        : 0;
      this.finance.term = this.financeElements.term.value
        ? Formatter.number(this.financeElements.term.value)
        : 0;
      this.finance.rate = this.financeElements.rate.value
        ? Formatter.number(this.financeElements.rate.value)
        : 0;
      this.finance.frequency = paymentFrequency;
    }

    // Update Lease Calculator
    if (this.hasLeaseCalculator) {
      this.lease.price = price;
      this.lease.tradein = tradein;
      this.lease.tax = tax;
      this.lease.down = this.leaseElements.down.value
        ? Formatter.number(this.leaseElements.down.value)
        : 0;
      this.lease.term = this.leaseElements.term.value
        ? Formatter.number(this.leaseElements.term.value)
        : 0;
      this.lease.rate = this.leaseElements.rate.value
        ? Formatter.number(this.leaseElements.rate.value)
        : 0;
      this.lease.residual = this.leaseElements.end.value
        ? Formatter.number(this.leaseElements.end.value)
        : 0;
      this.lease.frequency = paymentFrequency;
    }
  }

  calculate() {
    this.updateCalculators();

    if (this.hasFinanceCalculator) {
      this.finance.calculate();

      this.financeElements.balance.textContent = isFinite(this.finance.balance)
        ? this.formatVdpPrice(this.finance.balance)
        : '$--.--';

      this.financeElements.payment.textContent = isFinite(this.finance.payments)
        ? this.formatVdpPrice(this.finance.payments)
        : '$--.--';

      if (this.financeElements.cob) {
        this.financeElements.cob.textContent = isFinite(this.finance.costOfBorrowing)
          ? this.formatVdpPrice(this.finance.costOfBorrowing)
          : '$--.--';
      }
    }

    if (this.hasLeaseCalculator) {
      this.lease.calculate();

      this.leaseElements.balance.textContent = isFinite(this.lease.balance)
        ? this.formatVdpPrice(this.lease.balance)
        : '$--.--';

      this.leaseElements.payment.textContent = isFinite(this.lease.payments)
        ? this.formatVdpPrice(this.lease.payments)
        : '$--.--';

      if (this.leaseElements.cob) {
        this.leaseElements.cob.textContent = isFinite(this.lease.costOfBorrowing)
          ? this.formatVdpPrice(this.lease.costOfBorrowing)
          : '$--.--';
      }
    }
  }

  log(...args) {
    if (get(this.options, 'debug', false)) {
      console.log('[Payment Calculator]', ...args);
    }
  }
}
